/**
 * Starter Avatar — circular initials marker with a tinted background
 * matching the design's `.av-*` palette. No photo support: this product's
 * brand voice keeps avatars as initials only (see the design's "no fake
 * faces" rule under Imagery).
 *
 * `initials` is required; we don't try to derive it from a name string
 * here — UI code computes it explicitly, which keeps locale / two-vs-one
 * letter decisions out of this component.
 */
import { forwardRef, type HTMLAttributes } from 'react';
import styles from './Avatar.module.css';

/**
 * Tone families map to the design's `--tint-{tone}-{bg|fg}` pairs.
 *
 * `brand` (teal) and `gold` (brass) are the on-brand vintage tones; the rest are
 * the ledger semantics (green/warning/error/info) plus neutral.
 */
export type AvatarTone =
  | 'brand'
  | 'gold'
  | 'green'
  | 'neutral'
  | 'warning'
  | 'error'
  | 'info';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  readonly initials: string;
  readonly tone?: AvatarTone;
  readonly size?: 'sm' | 'md' | 'lg' | 'xl';
}

const TONE_CLASS: Record<AvatarTone, string> = {
  brand: styles.brand ?? '',
  gold: styles.gold ?? '',
  green: styles.green ?? '',
  neutral: styles.neutral ?? '',
  warning: styles.warning ?? '',
  error: styles.error ?? '',
  info: styles.info ?? '',
};
const SIZE_CLASS: Record<NonNullable<AvatarProps['size']>, string> = {
  sm: styles.sm ?? '',
  md: styles.md ?? '',
  lg: styles.lg ?? '',
  xl: styles.xl ?? '',
};

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { initials, tone = 'green', size = 'md', className, ...rest },
  ref,
) {
  const classes = [styles.root, TONE_CLASS[tone], SIZE_CLASS[size], className]
    .filter(Boolean)
    .join(' ');
  return (
    <span ref={ref} className={classes} aria-hidden {...rest}>
      {initials}
    </span>
  );
});
